import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, RefreshCw, Check, X, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { TRANSPORT_REQUEST_STATUS_TR, OFFER_STATUS_TR, trStatusVariant } from "@/lib/labels";
import { YhListingFormDialog, YH_MODEL_LABEL } from "@/components/admin/YhListingForm";
import { YhCustomerPanel } from "@/components/admin/YhCustomerPanel";
import { YhModel3Panel } from "@/components/admin/YhModel3Panel";
import { YhKvkkTextsDialog } from "@/components/admin/YhKvkkTexts";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");
const fmtTL = (n) => `${Number(n || 0).toLocaleString("tr-TR")} TL`;
const route = (r) => `${r.pickup_province}/${r.pickup_district} → ${r.delivery_province}/${r.delivery_district}`;
const statusText = (r) => (r.yh_model === "model_3" && r.status === "OPEN" ? "Yayında · Başvuru Bekliyor" : TRANSPORT_REQUEST_STATUS_TR[r.status] || r.status);
const STATUS_FILTERS = ["ALL", "DRAFT", "OPEN", "OFFER_ACCEPTED", "AWAITING_PICKUP", "PICKED_UP", "IN_TRANSIT", "DELIVERED", "COMPLETED", "CANCELLED"];

const act = async (fn, okMsg, after) => {
  try { await fn(); toast.success(okMsg); after(); }
  catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
};

const OfferRow = ({ o, canDecide, onAccept, onReject }) => (
  <div className="rounded-xl border p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`yh-offer-${o.id}`}>
    <div className="text-sm">
      <div className="font-semibold">{o.business_name || "Sağlayıcı"}{o.business_rating ? <span className="text-xs text-muted-foreground"> · ★ {o.business_rating} ({o.business_review_count})</span> : null}</div>
      <div className="text-xs text-muted-foreground">{o.vehicle_type} {o.vehicle_capacity ? `· ${o.vehicle_capacity}` : ""} · Alım: {fmtDate(o.pickup_at)}</div>
    </div>
    <div className="flex items-center gap-2">
      <span className="font-bold" data-testid={`yh-offer-price-${o.id}`}>{fmtTL(o.price_tl)}</span>
      {canDecide && o.status === "pending" ? (
        <>
          <Button size="sm" onClick={() => onAccept(o)} data-testid={`yh-offer-accept-${o.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" />Seç</Button>
          <Button size="sm" variant="outline" onClick={() => onReject(o)} data-testid={`yh-offer-reject-${o.id}`}><X className="w-4 h-4" /></Button>
        </>
      ) : <Badge variant="secondary" data-testid={`yh-offer-status-${o.id}`}>{OFFER_STATUS_TR[o.status] || o.status}</Badge>}
    </div>
  </div>
);

const Assignment = ({ d }) => (
  <div className="rounded-xl bg-muted/50 p-3 text-sm space-y-1" data-testid="yh-detail-assignment">
    <div><span className="text-muted-foreground">Seçilen sağlayıcı:</span> <b>{d.accepted_business_name || "—"}</b> · {fmtTL(d.accepted_price_tl)}</div>
    <div><span className="text-muted-foreground">Araç:</span> {d.assigned_vehicle?.name || "Henüz atanmadı"}</div>
    <div><span className="text-muted-foreground">Şoför:</span> {d.assigned_driver?.name || "Henüz atanmadı"}</div>
  </div>
);

const ListingDetailDialog = ({ rid, onClose, onChanged, onEdit }) => {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => {
    if (rid) api.get(`/admin/transport/listings/${rid}`).then(({ data }) => { setD(data); setFailed(false); }).catch(() => { setD(null); setFailed(true); });
  }, [rid]);
  useEffect(() => { setD(null); setFailed(false); load(); }, [load]);
  const refresh = () => { load(); onChanged(); };

  const accept = (o) => { if (window.confirm(`${o.business_name} teklifi (${fmtTL(o.price_tl)}) seçilsin mi? Diğer teklifler kapanacak.`)) act(() => api.post(`/admin/transport/listings/${rid}/offers/${o.id}/accept`), "Sağlayıcı seçildi, iş atandı", refresh); };
  const reject = (o) => act(() => api.post(`/admin/transport/listings/${rid}/offers/${o.id}/reject`), "Teklif reddedildi", refresh);
  const cancel = () => { if (window.confirm("İlan iptal edilsin mi? Bekleyen teklifler kapanacak.")) act(() => api.post(`/admin/transport/listings/${rid}/archive`), "İlan iptal edildi", refresh); };
  const complete = () => act(() => api.post(`/transport/requests/${rid}/status`, null, { params: { status: "COMPLETED" } }), "Teslimat onaylandı, iş tamamlandı", refresh);

  return (
    <Dialog open={!!rid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="yh-detail-dialog">
        <DialogHeader><DialogTitle>İlan Detayı</DialogTitle></DialogHeader>
        {failed ? <div className="text-sm text-red-600" data-testid="yh-detail-error">İlan bulunamadı veya yüklenemedi.</div> : !d ? <div className="text-sm text-muted-foreground">Yükleniyor…</div> : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="text-sm space-y-0.5">
                <div className="font-bold text-base" data-testid="yh-detail-route">{route(d)}</div>
                <div>{d.transport_category} · {d.quantity} adet · {d.running_condition}{d.make_model ? ` · ${d.make_model}` : ""}</div>
                <div className="text-muted-foreground">Son alım: {fmtDate(d.deadline_at)}</div>
                {(d.pickup_address || d.delivery_address) && <div className="text-xs text-muted-foreground">Adres: {d.pickup_address || "—"} → {d.delivery_address || "—"}</div>}
                {d.description && <div className="text-xs">{d.description}</div>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant={trStatusVariant(d.status)} data-testid="yh-detail-status">{statusText(d)}</Badge>
                <Badge variant="outline" data-testid="yh-detail-model">{YH_MODEL_LABEL[d.yh_model] || d.yh_model}</Badge>
              </div>
            </div>
            {d.yh_model !== "model_1" && <YhCustomerPanel d={d} onChanged={refresh} />}
            {d.yh_model === "model_3" && <YhModel3Panel d={d} onChanged={refresh} />}
            {!["OPEN", "DRAFT", "CANCELLED"].includes(d.status) && <Assignment d={d} />}
            <div className="flex flex-wrap gap-2">
              {["DRAFT", "OPEN", "OFFER_ACCEPTED"].includes(d.status) && <Button size="sm" variant="outline" onClick={() => { onClose(); onEdit(d); }} data-testid="yh-detail-edit"><Pencil className="w-4 h-4 mr-1" />Düzenle</Button>}
              {["DRAFT", "OPEN"].includes(d.status) && <Button size="sm" variant="outline" className="text-red-600" onClick={cancel} data-testid="yh-detail-cancel"><Ban className="w-4 h-4 mr-1" />İlanı İptal Et</Button>}
              {d.status === "DELIVERED" && <Button size="sm" onClick={complete} data-testid="yh-detail-complete" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Teslimatı Onayla</Button>}
            </div>
            {d.yh_model !== "model_3" && <div className="space-y-2">
              <div className="font-semibold text-sm">Teklifler ({d.offers?.length || 0})</div>
              {!d.offers?.length && <div className="text-sm text-muted-foreground" data-testid="yh-detail-no-offers">Henüz teklif yok. İlan taşıma havuzunda sağlayıcılara açık.</div>}
              {d.offers?.map((o) => <OfferRow key={o.id} o={o} canDecide={d.status === "OPEN"} onAccept={accept} onReject={reject} />)}
            </div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ListingRow = ({ r, onOpen, onEdit }) => (
  <div className="rounded-2xl border p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`yh-row-${r.id}`}>
    <div className="text-sm min-w-0">
      <div className="font-semibold flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="text-[10px]" data-testid={`yh-row-model-${r.id}`}>{{ model_1: "Model 1", model_2: "Model 2", model_3: "Model 3" }[r.yh_model] || r.yh_model}</Badge>
        {route(r)}
      </div>
      {r.yh_model !== "model_1" && <div className="text-xs">Müşteri: <b>{r.yh_customer_snapshot?.name}</b>{r.yh_linked_customer_id ? " · hesaba bağlı" : ""}{r.yh_model === "model_3" ? ` · ${(r.yh_claims || []).filter((c) => c.status === "pending").length} bekleyen başvuru` : ""}</div>}
      <div className="text-xs text-muted-foreground">{r.transport_category} · {r.quantity} adet · Son alım: {fmtDate(r.deadline_at)}</div>
      {r.accepted_business_name && <div className="text-xs">Sağlayıcı: <b>{r.accepted_business_name}</b> · {fmtTL(r.accepted_price_tl)}</div>}
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant={trStatusVariant(r.status)} data-testid={`yh-row-status-${r.id}`}>{statusText(r)}</Badge>
      {r.yh_model !== "model_3" && <Badge variant="secondary" data-testid={`yh-row-offers-${r.id}`}>{r.pending_offer_count} bekleyen / {r.offer_count} teklif</Badge>}
      <Button size="sm" variant="outline" onClick={() => onOpen(r.id)} data-testid={`yh-row-open-${r.id}`}><Eye className="w-4 h-4 mr-1" />Detay</Button>
      {["DRAFT", "OPEN", "OFFER_ACCEPTED"].includes(r.status) && <Button size="sm" variant="ghost" onClick={() => onEdit(r)} data-testid={`yh-row-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>}
    </div>
  </div>
);

export const AdminYhListings = () => {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [model, setModel] = useState("model_1,model_2,model_3");
  const [kvkkOpen, setKvkkOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(params.get("listing"));
  useEffect(() => {
    const l = params.get("listing");
    if (l) setDetailId(l);
  }, [params]);

  const load = useCallback(() => {
    api.get("/admin/transport/listings", { params: { yh_model: model, ...(status !== "ALL" ? { status } : {}) } })
      .then(({ data }) => setItems(data)).catch(() => toast.error("İlanlar yüklenemedi"));
  }, [status, model]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const openEdit = (r) => { setEditing(r); setFormOpen(true); };
  const closeDetail = () => { setDetailId(null); if (params.get("listing")) { params.delete("listing"); setParams(params, { replace: true }); } };

  return (
    <div className="space-y-4" data-testid="yh-listings-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-bold">Yol Hattı İş İlanları</h2>
          <p className="text-xs text-muted-foreground">Yol Hattı adına yayınlanan taşıma ilanları taşıma havuzunda görünür, sağlayıcılar teklif verir.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-40" data-testid="yh-model-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="model_1,model_2,model_3">Tüm modeller</SelectItem>
              <SelectItem value="model_1">Model 1</SelectItem>
              <SelectItem value="model_2">Model 2</SelectItem>
              <SelectItem value="model_3">Model 3</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setKvkkOpen(true)} data-testid="yh-kvkk-open">KVKK Metinleri</Button>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="yh-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_FILTERS.map((s) => <SelectItem key={s} value={s}>{s === "ALL" ? "Tüm durumlar" : TRANSPORT_REQUEST_STATUS_TR[s]}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="icon" variant="outline" onClick={load} data-testid="yh-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="yh-new-listing" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold"><Plus className="w-4 h-4 mr-1" />Yeni İlan</Button>
        </div>
      </div>
      {!items.length && <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="yh-empty">Henüz ilan yok. "Yeni İlan" ile Yol Hattı adına ilk taşıma ilanını yayınlayın.</div>}
      <div className="space-y-2">{items.map((r) => <ListingRow key={r.id} r={r} onOpen={setDetailId} onEdit={openEdit} />)}</div>
      <YhListingFormDialog open={formOpen} onClose={() => setFormOpen(false)} listing={editing} onSaved={load} />
      <ListingDetailDialog rid={detailId} onClose={closeDetail} onChanged={load} onEdit={openEdit} />
      <YhKvkkTextsDialog open={kvkkOpen} onClose={() => setKvkkOpen(false)} />
    </div>
  );
};
